import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  createCommand,
  type LexicalCommand,
} from 'lexical'
import { $createImageNode, $isImageNode } from '../nodes/image-node'

export const INSERT_IMAGE_COMMAND: LexicalCommand<{ src: string; altText?: string }> =
  createCommand('INSERT_IMAGE_COMMAND')

async function uploadFile(projectId: string, file: File): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await fetch(`/workspaces/${projectId}/uploads`, {
    method: 'POST',
    body: formData,
  })
  if (!res.ok) throw new Error('Upload failed')
  const data = (await res.json()) as { url: string }
  return data.url
}

async function handleImageFiles(
  editor: ReturnType<typeof useLexicalComposerContext>[0],
  projectId: string,
  files: File[]
) {
  const imageFiles = files.filter((f) => f.type.startsWith('image/'))
  for (const file of imageFiles) {
    try {
      const url = await uploadFile(projectId, file)
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, { src: url, altText: file.name })
    } catch {
      console.error('Image upload failed')
    }
  }
}

interface ImagePluginProps {
  projectId?: string
}

export function ImagePlugin({ projectId }: ImagePluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const removeInsertCommand = editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      (payload) => {
        const imageNode = $createImageNode({
          src: payload.src,
          altText: payload.altText ?? '',
        })
        $insertNodes([imageNode])
        return true
      },
      COMMAND_PRIORITY_HIGH
    )

    const removeFormatCommand = editor.registerCommand(
      FORMAT_ELEMENT_COMMAND,
      (format) => {
        const selection = $getSelection()
        if (!$isNodeSelection(selection)) return false

        const nodes = selection.getNodes()
        let handled = false
        for (const node of nodes) {
          if ($isImageNode(node)) {
            node.setFormat(format)
            handled = true
          }
        }
        return handled
      },
      COMMAND_PRIORITY_LOW
    )

    return () => {
      removeInsertCommand()
      removeFormatCommand()
    }
  }, [editor])

  useEffect(() => {
    if (!projectId) return

    const handlePaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? [])
      if (files.some((f) => f.type.startsWith('image/'))) {
        event.preventDefault()
        handleImageFiles(editor, projectId, files)
      }
    }

    const handleDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? [])
      if (files.some((f) => f.type.startsWith('image/'))) {
        event.preventDefault()
        handleImageFiles(editor, projectId, files)
      }
    }

    const rootElement = editor.getRootElement()
    if (!rootElement) return

    rootElement.addEventListener('paste', handlePaste)
    rootElement.addEventListener('drop', handleDrop)

    return () => {
      rootElement.removeEventListener('paste', handlePaste)
      rootElement.removeEventListener('drop', handleDrop)
    }
  }, [editor, projectId])

  return null
}
